import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Flag, Trophy, Plus, MapPin, ArrowRight, Crown, X, Target } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [rounds, setRounds] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [courses, setCourses] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [wins, setWins] = useState([]); // leagues we won and haven't seen
  // Seed the pill from the freshest cached handicap we already know about
  // (localStorage snapshot from the last /stats/me → falls back to the user
  // object's manual handicap). Without this seed the pill would flash in
  // 1-2 seconds after every home-page mount while the /stats/me Promise.all
  // resolved — which the user perceived as "old layout appears, then new
  // layout with handicap top-right overlays it".
  const cachedHandicap = (() => {
    try {
      const raw = localStorage.getItem(`fw_handicap_${user?.id || ""}`);
      if (raw !== null && raw !== "") {
        const n = parseFloat(raw);
        if (!Number.isNaN(n)) return n;
      }
    } catch { /* ignore */ }
    return typeof user?.handicap === "number" ? user.handicap : null;
  })();
  const [handicap, setHandicap] = useState(cachedHandicap);
  const [milestonePending, setMilestonePending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, l, c, f, s] = await Promise.all([
          api.get("/rounds"),
          api.get("/leagues/mine"),
          api.get("/courses"),
          api.get("/courses/featured"),
          api.get("/stats/me").catch(() => ({ data: null })),
        ]);
        setRounds(r.data || []);
        setLeagues(l.data || []);
        setCourses(c.data || []);
        setFeatured(f.data || null);
        const fresh = s.data?.handicap_index ?? null;
        setHandicap(fresh);
        setMilestonePending(!!s.data?.milestone);
        // Persist the fresh handicap so the next home-page visit paints
        // the pill instantly rather than waiting for the Promise.all.
        try {
          if (fresh === null || fresh === undefined) {
            localStorage.removeItem(`fw_handicap_${user?.id || ""}`);
          } else {
            localStorage.setItem(`fw_handicap_${user?.id || ""}`, String(fresh));
          }
        } catch { /* storage full / private mode */ }
        // Detect un-seen wins
        const mine = l.data || [];
        const winChecks = await Promise.all(
          mine.map(async (lg) => {
            try {
              const { data } = await api.get(`/leagues/${lg.id}`);
              const seen = (data.league.winner_seen_by || []).includes(user?.id);
              if (data.league_complete && data.winner?.user_id === user?.id && !seen) {
                return { league: data.league, winner: data.winner };
              }
            } catch { /* ignore */ }
            return null;
          })
        );
        setWins(winChecks.filter(Boolean));
      } catch {
        // ignore
      }
    })();
  }, [user?.id]);

  const dismissWin = async (leagueId) => {
    try { await api.post(`/leagues/${leagueId}/winner-seen`); } catch { /* ignore */ }
    setWins((prev) => prev.filter((w) => w.league.id !== leagueId));
  };

  const lastRound = rounds[0];
  const activeRound = rounds.find((r) => !r.completed);

  return (
    <div className="px-5 pt-6">
      {/* Top header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="label-uppercase">Welcome back</p>
          <h1 className="font-heading font-black tracking-tighter-2 text-3xl text-[var(--primary)]" data-testid="dashboard-user-name">
            {user?.name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {handicap !== null && handicap !== undefined && (
            <Link
              to="/app/stats"
              data-testid="handicap-pill"
              className="relative flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono-num font-black text-[var(--primary)] border border-[var(--primary)]/15 bg-[var(--accent-sand)]/40 active:scale-95 transition-transform"
              title={milestonePending ? "New handicap milestone — tap to celebrate" : "Your handicap index — tap for full stats"}
            >
              <Target size={14} weight="fill" className="text-[var(--accent-terracotta,#C4543C)]" />
              <span className="text-sm leading-none">{handicap.toFixed(1)}</span>
              <span className="text-[10px] uppercase tracking-wider opacity-70 leading-none">HCP</span>
              {milestonePending && (
                <span
                  data-testid="handicap-milestone-pulse"
                  className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--accent-terracotta,#C4543C)] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--accent-terracotta,#C4543C)]" />
                </span>
              )}
            </Link>
          )}
          <Link to="/app/profile" className="h-12 w-12 rounded-full bg-[var(--primary)] text-white font-heading font-bold flex items-center justify-center text-lg">
            {(user?.name || "?").slice(0,1).toUpperCase()}
          </Link>
        </div>
      </div>

      {/* Win celebration */}
      {wins.map((w) => {
        const symbol = { GBP: "£", USD: "$", EUR: "€" }[w.league.pot_currency] || "";
        return (
          <div key={w.league.id} className="surface-card overflow-hidden mb-5 bg-gradient-to-br from-[var(--accent-terracotta)] to-[#8a3a28] text-white relative" data-testid={`win-card-${w.league.id}`}>
            <Crown size={140} weight="fill" className="absolute -right-6 -bottom-6 opacity-15" />
            <div className="relative p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-white/80">🏆 You won!</p>
                  <h2 className="font-heading font-black tracking-tighter-2 text-3xl mt-1">{w.league.name}</h2>
                  {w.league.pot_amount != null && (
                    <p className="text-sm opacity-90 mt-1">Prize: {symbol}{w.league.pot_amount}</p>
                  )}
                </div>
                <button onClick={() => dismissWin(w.league.id)} data-testid={`win-dismiss-${w.league.id}`} className="text-white/80 hover:text-white">
                  <X size={20} weight="bold" />
                </button>
              </div>
              <Link to={`/app/leagues/${w.league.id}`} className="mt-3 inline-flex items-center gap-2 bg-white text-[var(--accent-terracotta)] font-heading font-bold rounded-full px-4 py-2 text-sm" data-testid={`win-view-${w.league.id}`}>
                View league <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        );
      })}

      {/* Resume in-progress round — shown above the "Start a round" CTA so
          a crashed/abandoned round is the first thing you see. */}
      {activeRound && (
        <Link
          to={`/app/round/${activeRound.id}`}
          data-testid="resume-round-card"
          className="block mb-5 surface-card-flat p-4 bg-[var(--accent-sand)] border-2 border-[var(--accent-terracotta)] active:scale-[0.98] transition-transform"
        >
          <p className="label-uppercase text-[var(--accent-terracotta)]">Round in progress</p>
          <div className="flex items-center justify-between mt-1">
            <span className="font-heading font-bold text-lg text-[var(--primary)]" data-testid="resume-round-name">{activeRound.course_name}</span>
            <span className="text-sm text-[var(--primary)] font-semibold">Resume →</span>
          </div>
          {(() => {
            const lastScored = (activeRound.scores || []).slice().sort((a, b) => b.hole_number - a.hole_number)[0];
            const nextHole = lastScored ? Math.min(18, lastScored.hole_number + 1) : 1;
            return (
              <p className="text-xs text-[var(--text-secondary)] mt-1.5" data-testid="resume-round-progress">
                {lastScored ? `Last score on hole ${lastScored.hole_number} · ` : ""}
                Pick up from hole {nextHole}
              </p>
            );
          })()}
        </Link>
      )}

      {/* Hero CTA */}
      <Link
        to="/app/courses"
        data-testid="start-round-card"
        className="block surface-card overflow-hidden mb-5 relative group"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/35918463/pexels-photo-35918463.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[var(--primary)]/95 via-[var(--primary)]/70 to-transparent" />
        </div>
        <div className="relative p-6 min-h-[180px] text-white flex flex-col justify-between">
          <div>
            <p className="label-uppercase text-white/80">Ready to play</p>
            <h2 className="font-heading font-black tracking-tighter-2 text-4xl mt-1 mb-2">Start a round</h2>
            <p className="text-white/85 text-sm max-w-[20ch]">Pick a course and we&apos;ll handle yardages and scoring.</p>
          </div>
          <div className="flex items-center gap-2 mt-4 self-start bg-white text-[var(--primary)] rounded-full px-4 py-2 font-semibold">
            Choose course <ArrowRight size={16} weight="bold" />
          </div>
        </div>
      </Link>

      {/* Free-tier round counter — loss aversion done right. Hidden once
          they're on Pro. Uses the same 2-round limit the backend enforces. */}
      {(() => {
        const isPro = user?.role === "admin" || ["active", "trialing"].includes(user?.subscription_status);
        if (isPro) return null;
        const FREE_LIMIT = 2;
        const used = Math.min(FREE_LIMIT, (rounds || []).length);
        const remaining = Math.max(0, FREE_LIMIT - used);
        const allUsed = remaining === 0;
        return (
          <Link
            to="/subscribe"
            data-testid="free-rounds-counter"
            className={`block rounded-2xl mb-5 p-4 transition-colors ${
              allUsed
                ? "bg-[var(--accent-terracotta)] text-white hover:bg-[var(--accent-terracotta)]/90"
                : "border-2 border-dashed border-[var(--primary)] bg-[var(--accent-sand)] hover:bg-[var(--accent-sand)]/70 text-[var(--primary)]"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm">
                  {allUsed
                    ? "You've used your 2 free rounds"
                    : `${used} of ${FREE_LIMIT} free rounds used`}
                </div>
                <div className={`text-xs mt-0.5 ${allUsed ? "text-white/85" : "text-[var(--text)]"}`}>
                  {allUsed
                    ? "Tap to unlock unlimited rounds with Fairway Pro."
                    : remaining === 1
                      ? "1 round left on free · tap to go Pro for unlimited"
                      : "Tap to see what you'll get with Pro"}
                </div>
              </div>
              <span className="font-mono-num font-black text-xl">
                {used}/{FREE_LIMIT}
              </span>
            </div>
            {!allUsed && (
              <div className="mt-2 h-1.5 rounded-full bg-[var(--primary)]/15 overflow-hidden">
                <div
                  className="h-full bg-[var(--primary)] transition-all"
                  style={{ width: `${(used / FREE_LIMIT) * 100}%` }}
                />
              </div>
            )}
          </Link>
        );
      })()}

      {/* Course of the week */}
      {featured && (
        <Link
          to={`/app/courses/${featured.id}`}
          data-testid="course-of-the-week"
          className="block surface-card-flat overflow-hidden mb-5 hover:shadow-lg transition-shadow"
        >
          <div className="flex">
            <div className="relative h-32 w-32 flex-shrink-0 overflow-hidden">
              {featured.image_url && <img src={featured.image_url} alt="" className="w-full h-full object-cover" />}
              <div className="absolute top-2 left-2 bg-[var(--accent-terracotta)] text-white text-[9px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                Featured
              </div>
            </div>
            <div className="flex-1 p-4 min-w-0">
              <p className="label-uppercase !text-[var(--accent-terracotta)]">Course of the week</p>
              <div className="font-heading font-bold text-base text-[var(--primary)] line-clamp-1 mt-1">{featured.name}</div>
              <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                <MapPin size={12} weight="fill" /> <span className="line-clamp-1">{featured.location}</span>
              </div>
              <div className="flex gap-3 mt-2 text-xs">
                <span><span className="label-uppercase">Par</span> <span className="font-mono-num font-bold">{featured.par_total ?? "—"}</span></span>
                <span><span className="label-uppercase">Yds</span> <span className="font-mono-num font-bold">{featured.yards_total ?? "—"}</span></span>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link to="/app/stats" data-testid="stat-rounds" className="surface-card-flat p-4 hover:shadow-md transition-shadow">
          <p className="label-uppercase">Rounds</p>
          <div className="font-mono-num font-black text-3xl text-[var(--primary)] mt-1">{rounds.filter(r=>r.completed).length}</div>
        </Link>
        <Link to="/app/stats" data-testid="stat-best" className="surface-card-flat p-4 hover:shadow-md transition-shadow">
          <p className="label-uppercase">Best (vs par)</p>
          <div className="font-mono-num font-black text-3xl text-[var(--primary)] mt-1">{bestScore(rounds)}</div>
        </Link>
      </div>

      {/* Recent round */}
      <SectionHeader title="Recent round" />
      {lastRound ? (
        <div className="surface-card-flat p-4 mb-6" data-testid="recent-round-card">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-heading font-bold text-[var(--primary)] text-lg">{lastRound.course_name}</div>
              <div className="text-sm text-[var(--text-secondary)]">{new Date(lastRound.started_at).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric"})}</div>
            </div>
            <div className="text-right">
              <div className="font-mono-num text-3xl font-black text-[var(--primary)]">{lastRound.total_strokes || "—"}</div>
              <div className="text-xs text-[var(--text-secondary)]">{lastRound.completed ? "Final" : "In progress"}</div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyHint text="No rounds yet. Start your first one." />
      )}

      {/* Leagues */}
      <SectionHeader
        title="Your leagues"
        action={
          <Link to="/app/leagues" data-testid="manage-leagues-link" className="text-sm font-semibold text-[var(--primary)] underline">
            Manage
          </Link>
        }
      />
      {leagues.length === 0 ? (
        <Link to="/app/leagues" data-testid="create-league-prompt" className="block surface-card-flat p-5 mb-6 border-dashed border-2 hover:bg-white/60 transition-colors">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[var(--secondary)] flex items-center justify-center"><Plus size={22} weight="bold" /></div>
            <div>
              <div className="font-heading font-bold text-[var(--primary)]">Create a league</div>
              <div className="text-sm text-[var(--text-secondary)]">Invite mates and battle round by round.</div>
            </div>
          </div>
        </Link>
      ) : (
        <div className="space-y-3 mb-6">
          {leagues.slice(0,2).map((lg) => (
            <Link
              to={`/app/leagues/${lg.id}`}
              key={lg.id}
              data-testid={`league-card-${lg.id}`}
              className="block surface-card-flat p-4 flex items-center justify-between hover:bg-white"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center">
                  <Trophy size={18} weight="fill" />
                </div>
                <div>
                  <div className="font-heading font-bold text-[var(--primary)]">{lg.name}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{lg.member_ids.length} player{lg.member_ids.length === 1 ? "" : "s"} · Code <span className="font-mono-num font-bold">{lg.code}</span></div>
                </div>
              </div>
              <ArrowRight size={18} weight="bold" className="text-[var(--text-secondary)]" />
            </Link>
          ))}
        </div>
      )}

      {/* Featured courses */}
      <SectionHeader
        title="Featured courses"
        action={<Link to="/app/courses" data-testid="see-all-courses" className="text-sm font-semibold text-[var(--primary)] underline">See all</Link>}
      />
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
        {courses.slice(0, 6).map((c) => (
          <Link
            to={`/app/courses/${c.id}`}
            key={c.id}
            data-testid={`featured-course-${c.id}`}
            className="min-w-[200px] surface-card-flat overflow-hidden"
          >
            <div className="h-24 bg-[var(--secondary)] overflow-hidden">
              {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-3">
              <div className="font-heading font-bold text-[var(--primary)] text-sm line-clamp-1">{c.name}</div>
              <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                <MapPin size={12} weight="fill" /> <span className="line-clamp-1">{c.location}</span>
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <span><span className="label-uppercase">Par</span> <span className="font-mono-num font-bold text-[var(--primary)]">{c.par_total ?? "—"}</span></span>
                <span><span className="label-uppercase">Yds</span> <span className="font-mono-num font-bold text-[var(--primary)]">{c.yards_total ?? "—"}</span></span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="surface-card-flat p-4" data-testid={testid}>
      <p className="label-uppercase">{label}</p>
      <div className="font-mono-num font-black text-3xl text-[var(--primary)] mt-1">{value}</div>
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
const _StatCard = StatCard;

function SectionHeader({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-heading font-bold text-lg text-[var(--primary)]">{title}</h3>
      {action}
    </div>
  );
}

function EmptyHint({ text }) {
  return <div className="surface-card-flat p-5 mb-6 text-[var(--text-secondary)] text-sm">{text}</div>;
}

function bestScore(rounds) {
  const completed = rounds.filter((r) => r.completed);
  if (!completed.length) return "—";
  const best = Math.min(...completed.map((r) => r.total_strokes - r.total_par));
  return (best >= 0 ? "+" : "") + best;
}
